/**
 * `<input type="datetime-local">` values: "YYYY-MM-DDTHH:mm" with no zone,
 * meant in the browser's own time.
 *
 * Sent as is, the Api read them with new Date() on a UTC server, so an admin
 * in Los Angeles who typed 23:59 got 23:59 UTC (16:59 her time), and every
 * save of an edit moved the stored time again by her offset. The form now
 * sends the instant, with the browser's offset applied here.
 */

/** The datetime-local value for a stored instant, in the browser's time. */
export function toLocalInput(iso: string | null | undefined): string {
  if (!iso) return '';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

/**
 * The instant a datetime-local value means in the browser's time, as an ISO
 * string with Z; '' is null (no time). A field left as it was prefilled sends
 * the stored instant back unchanged, seconds included (the input has none).
 * A value that does not parse is passed through for the Api to refuse.
 */
export function localInputToIso(value: string, stored?: string | null): string | null {
  if (!value) return null;
  if (stored && toLocalInput(stored) === value) return new Date(stored).toISOString();
  // A date-time with no zone is read in the browser's own zone.
  const d = new Date(value);
  return Number.isNaN(d.getTime()) ? value : d.toISOString();
}
